import { RestoreOutlineIcon } from '@carboncopyco/icons';
import { InlineLink, TextBase, TextCaption } from '@carboncopyco/ui';
import { useMemo, useState } from 'react';
import { VerticalTabsModal } from '../VerticalTabsModal';
import { SideTabs, SideTabsPanel } from './SideTabs';
import type { SettingsModalProps } from './types';
import { useLocalChanges } from './useLocalChanges';
export const SettingsModal = ({
  title,
  onClose,
  items,
  alertOnCloseUnsaved,
  open,
  initialItemKey = '',
  shouldCountChanges,
  onSubmit,
  initialData,
  onReset,
}: SettingsModalProps) => {
  const selectedItem = items.find((item) => item.label === initialItemKey);
  const firstItem = selectedItem ? initialItemKey : items[0].label;
  const [selectedId, setSelectedId] = useState<string>(firstItem);
  const {
    hasUnsavedChanges: changesCount,
    localChangesMap,
    resetChanges,
    performReset,
    handleSaveData,
    labelCounts,
    getSubValue,
    isLoading,
  } = useLocalChanges(selectedId, initialData);
  const { Content } = useMemo(() => {
    return items.find((item) => item.label === selectedId);
  }, [selectedId]);

  const resetData = () => {
    setSelectedId(firstItem);
    resetChanges();
  };

  const close = () => {
    onClose();
    resetData();
  };

  const runActionOnSave = () => {
    localChangesMap.forEach((subMap, key) => {
      const item = items.find((item) => item.label === key);
      const keysAction = item?.onSaveAll;
      if (keysAction) {
        keysAction(Object.fromEntries(subMap.entries()));
      }
      subMap.forEach((value, subKey) => {
        if (onSubmit) {
          onSubmit(key, subKey, value);
        }
        const initData = item?.initData;
        if (initData) {
          const subKeyAction = initData[subKey]?.onSaved;
          if (subKeyAction) {
            subKeyAction(value);
          }
        }
      });
    });
  };

  const onSave = () => {
    runActionOnSave();
    close();
  };

  const sideTabItems = items.map((item) => ({
    displayLabel: item.displayLabel,
    value: item.label,
    label: item.label,
    icon: item.icon,
  }));

  return (
    <VerticalTabsModal
      title={title}
      open={open}
      onSave={onSave}
      isSaveDisabled={!changesCount}
      confirmOnCancel={alertOnCloseUnsaved && !!changesCount}
      onClose={close}
      footerLeftContent={
        shouldCountChanges &&
        changesCount > 0 && (
          <div className="pt-0.5 me-auto">
            <TextBase
              label={
                labelCounts.length
                  ? labelCounts
                  : `${localChangesMap.size} changes`
              }
            />
          </div>
        )
      }
    >
      <SideTabs
        items={sideTabItems}
        currentValue={selectedId}
        onChange={(value) => setSelectedId(value)}
        extraSidebarContent={
          onReset && (
            <InlineLink
              as="button"
              onClick={() => performReset(onReset)}
              ellipsis
              label={
                <div className="pb-5 flex gap-1 px-5 [&_span]:hover:text-ds-primary hover:text-ds-primary">
                  <RestoreOutlineIcon size={18} />
                  <TextCaption
                    underline
                    label="Reset app settings"
                    color="text-ds-natural-600"
                  />
                </div>
              }
              color="text-ds-natural-600"
            />
          )
        }
      >
        {sideTabItems.map((item) => (
          <SideTabsPanel
            key={item.label}
            value={item.label}
            currentValue={selectedId}
          >
            {isLoading && Content ? null : (
              <div className="flex flex-col flex-1 overflow-y-auto px-8 gap-8 py-7">
                <Content
                  saveData={handleSaveData}
                  getValue={getSubValue}
                />
              </div>
            )}
          </SideTabsPanel>
        ))}
      </SideTabs>
    </VerticalTabsModal>
  );
};
